import { useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { BarChart3, Download, Search, RefreshCw } from "lucide-react";

const initialFilters = {
  date_from: "",
  date_to: "",
  subject: "",
  from_email: "",
  to_email: "",
};

const STAT_CARDS = [
  { key: "sent", label: "Sent" },
  { key: "delivered", label: "Delivered" },
  { key: "opened", label: "Opened" },
  { key: "clicked", label: "Clicked" },
  { key: "replied", label: "Replied" },
  { key: "bounced", label: "Bounced" },
  { key: "spam", label: "Spam" },
];

export default function Reports() {
  const [filters, setFilters] = useState(initialFilters);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const setF = (patch) => setFilters((f) => ({ ...f, ...patch }));

  const buildParams = () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    return params;
  };

  const runQuery = async () => {
    setLoading(true);
    try {
      const params = buildParams();
      const { data } = await api.get(`/sends/stats?${params.toString()}`);
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to fetch stats");
    } finally {
      setLoading(false);
    }
  };

  const downloadCsv = async () => {
    try {
      const params = buildParams();
      const token = localStorage.getItem("cf_token");
      const res = await fetch(`${API}/sends/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sends_export_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (err) {
      toast.error("Export failed");
    }
  };

  const reset = () => { setFilters(initialFilters); setData(null); };

  return (
    <div className="p-10 max-w-7xl cf-fade">
      <div className="mb-10 pb-6 border-b border-zinc-800">
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">// reports / cross_campaign</div>
        <h1 className="font-display text-5xl font-bold uppercase mt-2 tracking-tight">Stats &amp; Export</h1>
        <p className="text-zinc-400 text-sm mt-2 max-w-2xl">
          Slice sends across all campaigns by date range, subject, sender or recipient. Export the result as CSV.
        </p>
      </div>

      {/* Filters */}
      <div className="cf-card p-5 mb-6" data-testid="reports-filters">
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-4">// filters</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-2">Date from</label>
            <input
              type="date"
              data-testid="filter-date-from"
              value={filters.date_from}
              onChange={(e) => setF({ date_from: e.target.value })}
              className="cf-input"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-2">Date to</label>
            <input
              type="date"
              data-testid="filter-date-to"
              value={filters.date_to}
              onChange={(e) => setF({ date_to: e.target.value })}
              className="cf-input"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-2">Subject contains</label>
            <input
              type="text"
              data-testid="filter-subject"
              value={filters.subject}
              onChange={(e) => setF({ subject: e.target.value })}
              className="cf-input"
              placeholder="e.g. demo, follow-up"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-2">From email contains</label>
            <input
              type="text"
              data-testid="filter-from-email"
              value={filters.from_email}
              onChange={(e) => setF({ from_email: e.target.value })}
              className="cf-input"
              placeholder="hello@yourdomain.com"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-2">To email contains</label>
            <input
              type="text"
              data-testid="filter-to-email"
              value={filters.to_email}
              onChange={(e) => setF({ to_email: e.target.value })}
              className="cf-input"
              placeholder="@acme.com"
            />
          </div>
          <div className="flex items-end gap-2">
            <button
              type="button"
              onClick={runQuery}
              disabled={loading}
              data-testid="reports-run-button"
              className="cf-btn-primary"
            >
              <Search size={14} /> {loading ? "Loading..." : "Run query"}
            </button>
            <button
              type="button"
              onClick={reset}
              data-testid="reports-reset-button"
              className="cf-btn-secondary"
            >
              <RefreshCw size={12} /> Reset
            </button>
          </div>
        </div>
      </div>

      {/* Totals */}
      {data && (
        <div className="mb-6" data-testid="reports-totals">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">// totals / matched</div>
              <div className="text-white text-sm mt-1">
                <span className="font-mono">{data.total_matched ?? data.rows.length}</span> sends match the filter.
              </div>
            </div>
            <button
              type="button"
              onClick={downloadCsv}
              data-testid="reports-export-csv-button"
              className="cf-btn-primary"
            >
              <Download size={14} /> Download CSV
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-px bg-zinc-800 border border-zinc-800">
            {STAT_CARDS.map((s) => (
              <div key={s.key} className="bg-[#0A0A0A] p-4" data-testid={`stat-${s.key}`}>
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{s.label}</div>
                <div className="font-display text-3xl font-bold text-white mt-1">{data.totals[s.key] ?? 0}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Rows */}
      {data && (
        <div className="cf-card p-0 overflow-hidden" data-testid="reports-table-card">
          <div className="px-5 py-3 border-b border-zinc-800 flex items-center justify-between">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-2">
              <BarChart3 size={11} /> // rows / preview (first {data.rows.length})
            </div>
          </div>
          {data.rows.length === 0 ? (
            <div className="p-10 text-center text-zinc-500" data-testid="reports-empty">
              No sends match the filters. Try widening the date range or clearing fields.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="reports-table">
                <thead>
                  <tr className="text-left bg-zinc-950">
                    <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800">Campaign</th>
                    <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800">From</th>
                    <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800">To</th>
                    <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800">Subject</th>
                    <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800">Sent at</th>
                    <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800">Open</th>
                    <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800">Click</th>
                    <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800">Reply</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.id} className="border-b border-zinc-900 hover:bg-zinc-950/60" data-testid={`reports-row-${r.id}`}>
                      <td className="px-4 py-3 text-white truncate max-w-[180px]">{r.campaign_name || <em className="text-zinc-700">—</em>}</td>
                      <td className="px-4 py-3 text-zinc-400 text-xs">{r.from_email}</td>
                      <td className="px-4 py-3 text-zinc-300 text-xs">{r.to_email}</td>
                      <td className="px-4 py-3 text-zinc-400 truncate max-w-[260px]">{r.subject}</td>
                      <td className="px-4 py-3 text-zinc-500 font-mono text-xs">
                        {r.sent_at ? new Date(r.sent_at).toLocaleString() : "—"}
                      </td>
                      <td className="px-4 py-3">{r.opened ? <span className="text-emerald-400">●</span> : <span className="text-zinc-700">○</span>}</td>
                      <td className="px-4 py-3">{r.clicked ? <span className="text-emerald-400">●</span> : <span className="text-zinc-700">○</span>}</td>
                      <td className="px-4 py-3">{r.replied ? <span className="text-[#E23A28]">●</span> : <span className="text-zinc-700">○</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {!data && (
        <div className="cf-card p-10 text-center" data-testid="reports-prompt">
          <BarChart3 size={24} className="mx-auto text-zinc-600 mb-3" />
          <div className="text-white">Apply filters and click <span className="text-[#E23A28]">Run query</span> to see results.</div>
          <div className="text-zinc-500 text-sm mt-1">Leave a filter empty to ignore it. Date range is inclusive.</div>
        </div>
      )}
    </div>
  );
}
